import { FC, useEffect, useCallback, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { HiDocumentDownload } from 'react-icons/hi';
import { Button, Spinner, Table } from 'flowbite-react';

import { useGlobal } from '../../hooks/useGlobal';
import { CardContainer } from '../../layout';
import { getBonusRequestsStatistics, getStatistics } from '../../services';
import { canView } from '../../utils/permissions';

interface ITimePeriod {
  key: string;
  label: string;
}

interface IViewType {
  key: string;
  label: string;
  disabled?: boolean;
}

interface ITableFooterTotal {
  bahisal: number;
  betkanyon: number;
  unibahis: number;
  winxbet: number;
  winxbetBr: number;
  betloto: number;
  kasino: number;
  total: number;
  contactCardCount: number;
}

interface IStatistic {
  date: string;
  totalMessage: number;
  messageCountBySiteId: { [key: number]: number };
  contactCardCount: number;
}

const timePeriods: ITimePeriod[] = [
  { key: 'thisWeek', label: 'Bu Hafta' },
  { key: 'lastWeek', label: 'Geçen Hafta' },
  { key: 'thisMonth', label: 'Bu Ay' },
  { key: 'lastMonth', label: 'Geçen Ay' },
];

const viewTypes: IViewType[] = [
  { key: 'invites', label: 'Davetler' },
  { key: 'bonusRequests', label: 'Bonus İstekleri' },
  { key: 'users', label: 'Kullanıcılar', disabled: true },
];

const tableFooterTotalInit: ITableFooterTotal = {
  bahisal: 0,
  betkanyon: 0,
  unibahis: 0,
  winxbet: 0,
  winxbetBr: 0,
  betloto: 0,
  kasino: 0,
  total: 0,
  contactCardCount: 0,
};

export const StatisticsMain: FC = () => {
  const { user } = useGlobal();
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('thisMonth');
  const [selectedViewType, setSelectedViewType] = useState<string>('invites');
  const [statistics, setStatistics] = useState<IStatistic[]>([]);
  const [tableFooterTotal, setTableFooterTotal] =
    useState<ITableFooterTotal>(tableFooterTotalInit);

  const calculateTotals = (data: any) => {
    return data.reduce(
      (acc: ITableFooterTotal, item: IStatistic) => {
        acc.bahisal += item.messageCountBySiteId[3];
        acc.betkanyon += item.messageCountBySiteId[1];
        acc.unibahis += item.messageCountBySiteId[5];
        acc.winxbet += item.messageCountBySiteId[2];
        acc.winxbetBr += item.messageCountBySiteId[4];
        acc.betloto += item.messageCountBySiteId[7];
        acc.kasino += item.messageCountBySiteId[8];
        acc.total += item.totalMessage;
        acc.contactCardCount += item.contactCardCount;

        return acc;
      },
      {
        bahisal: 0,
        betkanyon: 0,
        unibahis: 0,
        winxbet: 0,
        winxbetBr: 0,
        betloto: 0,
        kasino: 0,
        total: 0,
        contactCardCount: 0,
      },
    );
  };

  // fetch statistics
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      let response: any;

      if (selectedViewType === 'invites') {
        response = await getStatistics({
          period: selectedPeriod,
        });
      } else if (selectedViewType === 'bonusRequests') {
        response = await getBonusRequestsStatistics({
          period: selectedPeriod,
        });
      }

      const tempTotals = calculateTotals(response.data);

      setTableFooterTotal(tempTotals);
      setStatistics(response.data);
    } catch (error) {
      console.error('fetchStatistics error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedViewType]);

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  const handleViewTypeChange = (viewType: string) => {
    setSelectedViewType(viewType);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Permission check moved to render section
  if (!canView(user, 'statistics')) {
    return <Navigate to="/" />;
  }

  return (
    <CardContainer title="İstatistikler">
      {/* date picker */}
      <div className="items-center flex justify-between">
        <div className="flex gap-4">
          <Button.Group>
            {viewTypes.map((viewType) => (
              <Button
                key={viewType.key}
                color={selectedViewType === viewType.key ? 'dark' : 'gray'}
                disabled={viewType.disabled}
                onClick={() =>
                  !viewType.disabled && handleViewTypeChange(viewType.key)
                }
              >
                {viewType.label}
              </Button>
            ))}
          </Button.Group>
        </div>
        <div className="flex gap-4">
          <Button.Group>
            {timePeriods.map((item) => (
              <Button
                color={selectedPeriod === item.key ? 'dark' : 'light'}
                key={item.key}
                onClick={() => handlePeriodChange(item.key)}
              >
                {item.label}
              </Button>
            ))}
          </Button.Group>
          <Button
            color="gray"
            as={Link}
            to={`${
              process.env.NODE_ENV === 'development'
                ? process.env.REACT_APP_BASE_URL_DEV
                : process.env.REACT_APP_BASE_URL
            }/statistics/excel-export?period=${selectedPeriod}`}
          >
            <HiDocumentDownload />
          </Button>
        </div>
      </div>
      {/* date picker */}
      <div className="mt-4">
        {loading ? (
          <div className="text-center">
            <Spinner />
          </div>
        ) : (
          <Table striped hoverable>
            <Table.Head>
              <Table.HeadCell></Table.HeadCell>
              <Table.HeadCell>BA</Table.HeadCell>
              <Table.HeadCell>BK</Table.HeadCell>
              {/* <Table.HeadCell>Unibahis</Table.HeadCell> */}
              <Table.HeadCell>WX</Table.HeadCell>
              {/* <Table.HeadCell>WinxbetBr</Table.HeadCell> */}
              <Table.HeadCell>BL</Table.HeadCell>
              <Table.HeadCell>KA</Table.HeadCell>
              <Table.HeadCell>Mesaj</Table.HeadCell>
              {selectedViewType === 'invites' && (
                <Table.HeadCell>Kişi Kartı</Table.HeadCell>
              )}
            </Table.Head>
            <Table.Body>
              {statistics.map((item: IStatistic) => {
                const {
                  date,
                  totalMessage,
                  messageCountBySiteId,
                  contactCardCount,
                } = item;

                return (
                  <Table.Row key={date}>
                    <Table.Cell>{date}</Table.Cell>
                    <Table.Cell>{messageCountBySiteId[3]}</Table.Cell>
                    <Table.Cell>{messageCountBySiteId[1]}</Table.Cell>
                    {/* <Table.Cell>{messageCountBySiteId[5]}</Table.Cell> */}
                    <Table.Cell>{messageCountBySiteId[2]}</Table.Cell>
                    {/* <Table.Cell>{messageCountBySiteId[4]}</Table.Cell> */}
                    <Table.Cell>{messageCountBySiteId[7]}</Table.Cell>
                    <Table.Cell>{messageCountBySiteId[8]}</Table.Cell>
                    <Table.Cell>{totalMessage}</Table.Cell>
                    {selectedViewType === 'invites' && (
                      <Table.Cell>{contactCardCount}</Table.Cell>
                    )}
                  </Table.Row>
                );
              })}
              <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell>{tableFooterTotal.bahisal}</Table.Cell>
                <Table.Cell>{tableFooterTotal.betkanyon}</Table.Cell>
                {/* <Table.Cell>{tableFooterTotal.unibahis}</Table.Cell> */}
                <Table.Cell>{tableFooterTotal.winxbet}</Table.Cell>
                {/* <Table.Cell>{tableFooterTotal.winxbetBr}</Table.Cell> */}
                <Table.Cell>{tableFooterTotal.betloto}</Table.Cell>
                <Table.Cell>{tableFooterTotal.kasino}</Table.Cell>
                <Table.Cell>{tableFooterTotal.total}</Table.Cell>
                {selectedViewType === 'invites' && (
                  <Table.Cell>{tableFooterTotal.contactCardCount}</Table.Cell>
                )}
              </Table.Row>
            </Table.Body>
          </Table>
        )}
      </div>
    </CardContainer>
  );
};
